/**
 * Drop-in audited replacements for `addDoc` / `updateDoc` / `deleteDoc`.
 *
 * Several admin pages write Firestore directly rather than through a service —
 * PathwayBuilder alone has 23 such call sites. Instrumenting each one by hand
 * means 23 chances to get the entity name or the previous-value read wrong, so
 * these wrappers derive the audit coordinates from the reference path instead:
 *
 *   deviceSetups/{id}                            -> global catalog
 *   tenants/{tid}/pathways/{id}                  -> tenant config
 *   tenants/{tid}/patients/{pid}/readings/{id}   -> patient-scoped
 *
 * Call sites then only need to swap the import. Pass `{ category }` when the
 * collection name is not a useful functional grouping (e.g. `pathwaySteps`
 * belongs to the `pathway` category).
 */
import { addDoc, updateDoc, deleteDoc, setDoc, getDoc } from 'firebase/firestore';
import {
  logAuditEvent,
  AUDIT_SCOPE,
  computeFieldChanges,
  snapshotForDelete,
} from './auditService';

/**
 * Collections whose functional area differs from their name, so related rows
 * group together in the log instead of fragmenting per collection.
 */
const CATEGORY_BY_COLLECTION = {
  pathways: 'pathway',
  pathwaySteps: 'pathway',
  billingRules: 'billing_setup',
  cptCodes: 'billing_setup',
  reimbursementMethods: 'billing_setup',
  deviceSetups: 'device_setup',
  clients: 'organization',
  clientSites: 'organization',
  providers: 'organization',
  insurances: 'organization',
  serviceTypes: 'organization',
  encounterTemplateCatalog: 'encounter_template',
  messageTemplates: 'messaging',
  bulkMessageJobs: 'messaging',
  programHoldReasons: 'program_reasons',
  programDischargeReasons: 'program_reasons',
  deactivationReasonsPrimary: 'deactivation_reasons',
  deactivationReasonsSecondary: 'deactivation_reasons',
  tenants: 'tenant_settings',
};

/**
 * Timestamps and actor stamps move on every write, so reporting them as changes
 * would make every row claim a change even when nothing meaningful moved.
 */
const STAMP_FIELDS = new Set([
  'updatedAt',
  'updatedBy',
  'updatedByUid',
  'lastModifiedAt',
  'lastModifiedBy',
]);

/**
 * `serverTimestamp()` and `deleteField()` return sentinels that Firestore only
 * accepts in a real write; echoing one into an audit row throws and would cost
 * us the row. Replace them with a readable marker.
 */
function sanitizeSentinels(value) {
  if (value === undefined) return null;
  if (value === null || typeof value !== 'object') return value;
  const methodName = value._methodName;
  if (typeof methodName === 'string') {
    return methodName === 'deleteField' ? null : `<${methodName}>`;
  }
  if (Array.isArray(value)) return value.map((v) => sanitizeSentinels(v));
  if (value.constructor === Object) {
    const out = {};
    for (const key of Object.keys(value)) out[key] = sanitizeSentinels(value[key]);
    return out;
  }
  return value;
}

function stripStamps(updates) {
  const out = {};
  for (const key of Object.keys(updates || {})) {
    if (!STAMP_FIELDS.has(key)) out[key] = sanitizeSentinels(updates[key]);
  }
  return out;
}

/**
 * Derive `{ scope, tenantId, patientId, collectionName }` from a Firestore
 * reference path. Returns nulls for shapes we do not recognize, which downgrades
 * the row to a global entry rather than dropping it.
 *
 * Exported for tests: this parse decides which log a row lands in, and a wrong
 * answer silently routes tenant data into the cross-tenant global log.
 */
export function describeRefPath(path, isDocRef) {
  const segments = String(path || '').split('/').filter(Boolean);
  // A doc ref ends in an id; drop it so the remaining tail is the collection.
  const collectionSegments = isDocRef ? segments.slice(0, -1) : segments;

  /**
   * `tenants/{tid}` itself — several settings live as fields on the tenant doc
   * rather than in a subcollection, and those belong to that tenant's log.
   */
  if (isDocRef && segments.length === 2 && segments[0] === 'tenants') {
    return {
      scope: AUDIT_SCOPE.TENANT,
      tenantId: segments[1],
      patientId: null,
      collectionName: 'tenants',
    };
  }

  /** `tenants/{tid}/patients/{pid}` itself — belongs on that patient's timeline. */
  if (isDocRef && segments.length === 4 && segments[0] === 'tenants' && segments[2] === 'patients') {
    return {
      scope: AUDIT_SCOPE.PATIENT,
      tenantId: segments[1],
      patientId: segments[3],
      collectionName: 'patients',
    };
  }

  if (collectionSegments[0] === 'tenants' && collectionSegments.length >= 3) {
    if (collectionSegments[2] === 'patients' && collectionSegments.length >= 5) {
      return {
        scope: AUDIT_SCOPE.PATIENT,
        tenantId: collectionSegments[1],
        patientId: collectionSegments[3],
        collectionName: collectionSegments[4],
      };
    }
    return {
      scope: AUDIT_SCOPE.TENANT,
      tenantId: collectionSegments[1],
      patientId: null,
      collectionName: collectionSegments[2],
    };
  }

  return {
    scope: AUDIT_SCOPE.GLOBAL,
    tenantId: null,
    patientId: null,
    collectionName: collectionSegments[collectionSegments.length - 1] || null,
  };
}

function auditArgsFor(ref, isDocRef, opts) {
  const { scope, tenantId, patientId, collectionName } = describeRefPath(
    typeof ref?.path === 'string' ? ref.path : '',
    isDocRef,
  );
  return {
    scope,
    tenantId,
    patientId,
    entity: opts?.entity || collectionName,
    category:
      opts?.category
      || CATEGORY_BY_COLLECTION[collectionName]
      || collectionName
      || 'unknown',
  };
}

/** `addDoc`, plus a create row carrying the full written document. */
export async function auditedAddDoc(colRef, data, opts = null) {
  const ref = await addDoc(colRef, data);
  const args = auditArgsFor(colRef, false, opts);
  await logAuditEvent({
    ...args,
    action: opts?.action || 'create',
    entityId: ref.id,
    snapshot: sanitizeSentinels(data),
    details: opts?.details || null,
  });
  return ref;
}

/**
 * `updateDoc`, plus an update row with before/after values. Reads the document
 * first so the row can answer "changed from what"; a failed read degrades to a
 * row without `changes` rather than blocking the update.
 */
export async function auditedUpdateDoc(docRef, updates, opts = null) {
  let previous = null;
  if (opts?.previous !== undefined) {
    previous = opts.previous;
  } else {
    try {
      const snap = await getDoc(docRef);
      previous = snap.exists() ? snap.data() : null;
    } catch {
      previous = null;
    }
  }

  await updateDoc(docRef, updates);

  const args = auditArgsFor(docRef, true, opts);
  await logAuditEvent({
    ...args,
    action: opts?.action || 'update',
    entityId: docRef.id,
    changes: computeFieldChanges(previous, stripStamps(updates)),
    details: opts?.details || null,
  });
}

/**
 * `setDoc`, plus a row describing the write. Settings docs are usually written
 * with `{ merge: true }` and may not exist yet, so this reports a create when
 * there was no prior document and an update otherwise.
 */
export async function auditedSetDoc(docRef, data, setOptions = undefined, opts = null) {
  let previous = null;
  try {
    const snap = await getDoc(docRef);
    previous = snap.exists() ? snap.data() : null;
  } catch {
    previous = null;
  }

  if (setOptions) await setDoc(docRef, data, setOptions);
  else await setDoc(docRef, data);

  const args = auditArgsFor(docRef, true, opts);
  await logAuditEvent({
    ...args,
    action: opts?.action || (previous ? 'update' : 'create'),
    entityId: docRef.id,
    changes: previous ? computeFieldChanges(previous, stripStamps(data)) : null,
    snapshot: previous ? null : sanitizeSentinels(data),
    details: opts?.details || null,
  });
}

/**
 * `deleteDoc`, plus a delete row snapshotting the document. A delete row that
 * cannot say what was destroyed is not useful for reconstructing it, so this
 * reads before removing.
 */
export async function auditedDeleteDoc(docRef, opts = null) {
  let previous = null;
  try {
    const snap = await getDoc(docRef);
    previous = snap.exists() ? snap.data() : null;
  } catch {
    previous = null;
  }

  await deleteDoc(docRef);

  const args = auditArgsFor(docRef, true, opts);
  await logAuditEvent({
    ...args,
    action: opts?.action || 'delete',
    entityId: docRef.id,
    snapshot: snapshotForDelete(previous),
    details: opts?.details || null,
  });
}
